/**
 * The one canvas file-ingestion pipeline.
 *
 * Every surface that drops file bytes onto a canvas — the toolbar "U" button,
 * the quick-add menu (immediate + deferred click-to-place), OS-clipboard paste,
 * and drag-drop — funnels through `ingestFiles` here. Before this hook the same
 * "plan → probe dimensions → mint ghost node → enqueue → swap ghost for real
 * record" sequence was copy-pasted into five near-identical implementations,
 * each with slightly different placement, conflict handling, and toast copy.
 *
 * What stays consistent across every caller now:
 *   - HEIC/HEIF planning + naming (`getPlannedUploadFile`)
 *   - natural-dimension probing + node-box fitting
 *   - the optimistic ghost node + local preview, swapped for the real entity on
 *     `onRecordCreated` and torn down on failure
 *   - the Skip / Replace / Keep-both conflict resolver (opt-in per call, but the
 *     same flow wherever it runs)
 *
 * What each caller still chooses: where the nodes land (`placement`) and whether
 * to run the conflict resolver (`resolveConflicts`).
 */

import { useFileConflictResolver } from "@/components/FileConflictResolver";
import {
  type EnqueueFile,
  useFileRecordUploadQueue,
} from "@/hooks/useFileRecordUploadQueue";
import {
  CANVAS_SNAP,
  CANVAS_SPACING,
  snapToGrid,
} from "@/pages/canvas/canvas-controls.config";
import { useCanvasNodePlacement } from "@/pages/canvas/hooks/useCanvasNodePlacement";
import { DEFAULT_SECTION_COLOR } from "@/pages/canvas/nodes/shared/CanvasColorPalettes";
import { layoutGridAroundCursor } from "@/pages/canvas/utils/canvasGridDropLayout";
import {
  buildFileRecordEntityForCanvas,
  defaultFileRecordNodeBox,
  fitImageNodeSize,
  readImageFileDimensions,
} from "@/pages/canvas/utils/imageDimensions";
import { buildSectionRectAroundSlots } from "@/pages/canvas/utils/sectionDropLayout";
import { getPlannedUploadFile } from "@/utils/imageFilePrep";
import type { Node } from "@xyflow/react";
import mime from "mime-types";
import { useCallback } from "react";
import toast from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";
import { useCanvas } from "../context/CanvasContext";
import { useCanvasLocalFilePreviewActions } from "../context/CanvasLocalFilePreviewContext";

type UploadMeta = {
  nodeId: string;
  position: { x: number; y: number };
  contentType: string;
  naturalWidth?: number;
  naturalHeight?: number;
};

/** A file resolved down to what we need to place + upload it. */
interface IngestEntry {
  file: File;
  displayName: string;
  contentType?: string;
  /** Set when the conflict resolver settled on a destination path. */
  resolvedPath?: string;
  kind: "create" | "replace";
}

/** An {@link IngestEntry} with its probed natural dimensions + fitted node box. */
interface IngestSlot extends IngestEntry {
  mimeType: string;
  naturalWidth?: number;
  naturalHeight?: number;
  nodeWidth: number;
  nodeHeight: number;
}

/**
 * Where the ingested nodes land.
 *  - `grid`: tidy square grid centered on `at` (drag-drop).
 *  - `stack`: vertical cascade downward from `at` (OS-clipboard paste).
 *  - `cascade`: caller-driven per-node positions (quick-add diagonal cascade).
 *  - `flow`: next free viewport slot, size-aware (toolbar). Placed one-by-one so
 *    each node reserves space against the ones already committed in this batch.
 */
export type CanvasFilePlacement =
  | { strategy: "grid"; at: { x: number; y: number } }
  | {
      strategy: "gridInSection";
      at: { x: number; y: number };
      sectionName: string;
    }
  | { strategy: "stack"; at: { x: number; y: number } }
  | { strategy: "cascade"; next: () => { x: number; y: number } }
  | { strategy: "flow" };

export interface IngestFilesOptions {
  placement: CanvasFilePlacement;
  /**
   * Run the Skip / Replace / Keep-both conflict resolver before placing.
   * Defaults to `true` so duplicate-named uploads are reconciled consistently
   * on every surface. The resolver may drop (Skip) or rename (Keep both) files
   * and returns `null` if the user cancels the batch.
   */
  resolveConflicts?: boolean;
  /** Toast shown when an individual file's upload fails. */
  errorMessage?: string;
}

export function useCanvasFileIngest(canvasId: string | undefined) {
  const queue = useFileRecordUploadQueue();
  const { addNode, removeNode, replaceNodeData } = useCanvas();
  const { register: registerLocalPreview, revoke: revokeLocalPreview } =
    useCanvasLocalFilePreviewActions();
  const { resolveUploads } = useFileConflictResolver();
  const { getNextNodePosition } = useCanvasNodePlacement();

  // Probe one file's natural dimensions and fit it into a node box. Mirrors the
  // per-file logic the five surfaces each carried inline.
  const probeSlot = useCallback(
    async (entry: IngestEntry): Promise<IngestSlot> => {
      const mimeType =
        entry.contentType ||
        entry.file.type ||
        mime.lookup(entry.file.name) ||
        "application/octet-stream";
      let dims: { width: number; height: number } | null = null;
      if (mimeType.startsWith("image/")) {
        try {
          dims = await readImageFileDimensions(entry.file);
        } catch {
          dims = null;
        }
      }
      const box =
        dims && dims.width > 0 && dims.height > 0
          ? fitImageNodeSize(dims.width, dims.height)
          : defaultFileRecordNodeBox(mimeType);
      return {
        ...entry,
        mimeType,
        naturalWidth: dims?.width,
        naturalHeight: dims?.height,
        nodeWidth: box.width,
        nodeHeight: box.height,
      };
    },
    [],
  );

  // Mint the optimistic ghost node + local preview and return the queue entry.
  const mintGhost = useCallback(
    (
      slot: IngestSlot,
      position: { x: number; y: number },
      parentId?: string,
    ) => {
      const nodeId = uuidv4();
      addNode({
        id: nodeId,
        type: "entity",
        position,
        ...(parentId ? { parentId } : {}),
        width: slot.nodeWidth,
        height: slot.nodeHeight,
        data: {
          loading: true,
          entity: {
            type: "file_record",
            base: "file_record",
            platform: "cohesive",
            // content_type rides on the ghost so the skeleton takes the same
            // shape the card will (page vs media) before the record exists.
            data: {
              id: "",
              title: slot.displayName ?? slot.file.name,
              content_type: slot.mimeType,
            },
          },
        },
      } as Node);
      registerLocalPreview(nodeId, slot.file, slot.mimeType);
      const entry: EnqueueFile<UploadMeta> = {
        file: slot.file,
        displayName: slot.displayName,
        contentType: slot.mimeType,
        kind: slot.kind,
        ...(slot.resolvedPath ? { resolvedPath: slot.resolvedPath } : {}),
        meta: {
          nodeId,
          position,
          contentType: slot.mimeType,
          naturalWidth: slot.naturalWidth,
          naturalHeight: slot.naturalHeight,
        },
      };
      return entry;
    },
    [addNode, registerLocalPreview],
  );

  const runEnqueue = useCallback(
    (enqueueFiles: EnqueueFile<UploadMeta>[], errorMessage: string) => {
      if (!canvasId || enqueueFiles.length === 0) return;
      void queue.enqueue<UploadMeta>(
        enqueueFiles,
        { class: "canvas", canvas_id: canvasId },
        {
          method: "canvas",
          onRecordCreated: (record, { meta }) => {
            if (!meta) return;
            const { entity } = buildFileRecordEntityForCanvas({
              record,
              naturalWidth: meta.naturalWidth,
              naturalHeight: meta.naturalHeight,
              contentType: meta.contentType,
            });
            replaceNodeData(meta.nodeId, { entity });
          },
          onUploadError: (_err, { meta }) => {
            if (meta) {
              revokeLocalPreview(meta.nodeId);
              removeNode(meta.nodeId);
            }
            toast.error(errorMessage);
          },
        },
      );
    },
    [canvasId, queue, replaceNodeData, revokeLocalPreview, removeNode],
  );

  const ingestFiles = useCallback(
    async (files: File[], options: IngestFilesOptions) => {
      if (!canvasId || files.length === 0) return;
      const {
        placement,
        resolveConflicts = true,
        errorMessage = "Failed to upload file",
      } = options;

      // Step 1 — resolve duplicate-name conflicts (Skip / Replace / Keep both).
      // The resolver plans HEIC→.jpg names itself, so when it runs we take the
      // name/contentType/path from its ops; otherwise we plan locally.
      let entries: IngestEntry[];
      if (resolveConflicts) {
        const resolved = await resolveUploads({
          uploads: files.map((file) => ({ file, destinationParentPath: "" })),
          scope: { class: "canvas", canvas_id: canvasId },
        });
        if (!resolved || resolved.length === 0) return;
        entries = resolved.map((op) => ({
          file: op.file,
          displayName: op.name,
          contentType: op.contentType,
          resolvedPath: op.path,
          kind: op.kind,
        }));
      } else {
        entries = files.map((file) => {
          const planned = getPlannedUploadFile(file);
          return {
            file,
            displayName: planned.name,
            contentType: planned.contentType,
            kind: "create" as const,
          };
        });
      }

      // Step 2 + 3 — probe, place, mint ghosts. `flow` places one-by-one so
      // each node reserves viewport space against those already added in this
      // batch; the others compute every position up front from the probed boxes.
      if (placement.strategy === "flow") {
        const enqueueFiles: EnqueueFile<UploadMeta>[] = [];
        for (const entry of entries) {
          const slot = await probeSlot(entry);
          const position = getNextNodePosition(slot.nodeWidth, slot.nodeHeight);
          enqueueFiles.push(mintGhost(slot, position));
        }
        runEnqueue(enqueueFiles, errorMessage);
        return;
      }

      const slots = await Promise.all(entries.map(probeSlot));

      if (placement.strategy === "gridInSection") {
        const absPositions = layoutGridAroundCursor(slots, placement.at);
        const sectionRect = buildSectionRectAroundSlots(
          slots.map((slot, i) => ({
            position: absPositions[i],
            width: slot.nodeWidth,
            height: slot.nodeHeight,
          })),
        );
        const sectionId = uuidv4();
        addNode({
          id: sectionId,
          type: "section",
          position: sectionRect.position,
          width: sectionRect.width,
          height: sectionRect.height,
          zIndex: -1,
          data: {
            title: placement.sectionName || "Section",
            sectionColor: DEFAULT_SECTION_COLOR,
            noFill: true,
            borderStyle: "solid",
            borderWidth: 2,
          },
        } as Node);
        const enqueueFiles = slots.map((slot, i) =>
          mintGhost(
            slot,
            {
              x: absPositions[i].x - sectionRect.position.x,
              y: absPositions[i].y - sectionRect.position.y,
            },
            sectionId,
          ),
        );
        runEnqueue(enqueueFiles, errorMessage);
        return;
      }

      const positions = placePositions(slots, placement);
      const enqueueFiles = slots.map((slot, i) =>
        mintGhost(slot, positions[i]),
      );
      runEnqueue(enqueueFiles, errorMessage);
    },
    [
      canvasId,
      resolveUploads,
      probeSlot,
      mintGhost,
      runEnqueue,
      getNextNodePosition,
      addNode,
    ],
  );

  return { ingestFiles };
}

/** Compute final positions for the up-front (non-`flow`) placement strategies. */
function placePositions(
  slots: IngestSlot[],
  placement: Exclude<
    CanvasFilePlacement,
    { strategy: "flow" } | { strategy: "gridInSection" }
  >,
): Array<{ x: number; y: number }> {
  switch (placement.strategy) {
    case "grid":
      return layoutGridAroundCursor(slots, placement.at);
    case "cascade":
      return slots.map(() => placement.next());
    case "stack": {
      const [snapX, snapY] = CANVAS_SNAP.grid;
      return slots.map((slot, i) => ({
        x: snapToGrid(placement.at.x, snapX),
        y: snapToGrid(
          placement.at.y + i * (slot.nodeHeight + CANVAS_SPACING.nodeGap),
          snapY,
        ),
      }));
    }
  }
}
